import { Injectable, NotFoundException, BadRequestException } from '@nestjs/common';
import { PrismaService } from '@cart/api-data-access';
import { RESERVED_SUBDOMAINS, SubdomainAvailability, validateSubdomainFormat } from '@cart/shared-types';

// The wildcard domain every merchant subdomain lives under — see plan.md §2.2
// (Caddy) / docs/apache-reverse-proxy.conf (current Apache adaptation).
const TENANT_DOMAIN_SUFFIX = process.env.TENANT_DOMAIN_SUFFIX ?? 'forgestack.in';

// The 5 canonical Indian GST slabs — every new store starts with these, so
// Tax Management → Tax Slab Profiles isn't empty the moment a merchant
// creates their first store. Mirrors prisma/seed.ts's own GST_SLABS
// (store_demo's copy) exactly — keep the two in sync if the canonical rates
// ever change. cgstRateBps + sgstRateBps always sum to igstRateBps, the
// invariant the admin Tax Management UI's "Live Guardrail Validator" checks
// on every save. See prisma/migrations/manual/024_backfill_default_tax_slabs.sql
// for the one-time backfill this same list was used for on stores that
// already existed before this fix.
const DEFAULT_TAX_SLABS = [
  { code: 'gst-zero-rated', name: 'GST Zero-Rated', hsnGroup: 'Essential Items', igst: 0, cgst: 0, sgst: 0 },
  { code: 'gst-apparel-low', name: 'GST Apparel Low', hsnGroup: 'Garments < ₹1000', igst: 500, cgst: 250, sgst: 250 },
  { code: 'gst-standard-1', name: 'GST Standard 1', hsnGroup: 'Mass Consumer Goods', igst: 1200, cgst: 600, sgst: 600 },
  { code: 'gst-standard-2', name: 'GST Standard 2', hsnGroup: 'Electronics / General Services', igst: 1800, cgst: 900, sgst: 900 },
  { code: 'gst-luxury-demerit', name: 'GST Luxury/Demerit', hsnGroup: 'Automobiles / Luxury Tech', igst: 2800, cgst: 1400, sgst: 1400 },
];

@Injectable()
export class MerchantService {
  constructor(private readonly prisma: PrismaService) {}

  private async resolveOrgId(userId: string): Promise<string> {
    const staff = await this.prisma.storeStaff.findFirst({
      where: { userId, isActive: true, deletedAt: null },
      select: { organizationId: true },
    });
    if (staff?.organizationId) return staff.organizationId;

    // A merchant with zero stores has no StoreStaff row yet (those are only
    // created as a side effect of createStore()/addStaff()) — fall back to
    // the FK set directly on User at registerMerchant() time. Gated on
    // approval so a pending/rejected merchant still can't slip through here.
    const user = await this.prisma.user.findUnique({
      where: { id: userId },
      select: { organizationId: true },
    });
    if (user?.organizationId) {
      const org = await this.prisma.organization.findUnique({
        where: { id: user.organizationId },
        select: { approvalStatus: true },
      });
      if (org?.approvalStatus === 'approved') return user.organizationId;
    }

    throw new NotFoundException('No organization found for this user');
  }

  async getOverview(userId: string) {
    const orgId = await this.resolveOrgId(userId);

    const org = await this.prisma.organization.findUniqueOrThrow({
      where: { id: orgId },
      select: {
        id: true, name: true, slug: true, email: true, phone: true,
        plan: true, planExpiresAt: true,
        isActive: true, suspendedAt: true, suspensionReason: true,
        stores: {
          where: { deletedAt: null },
          select: {
            id: true, name: true, slug: true, isActive: true,
            city: true, state: true, deactivatedAt: true,
            _count: { select: { staff: { where: { isActive: true, deletedAt: null } } } },
            domains: { where: { isPrimary: true }, select: { domain: true }, take: 1 },
          },
          orderBy: { name: 'asc' },
        },
      },
    });

    return {
      ...org,
      stores: org.stores.map(s => ({
        id: s.id, name: s.name, slug: s.slug, isActive: s.isActive,
        city: s.city, state: s.state, deactivatedAt: s.deactivatedAt,
        staffCount: s._count.staff,
        domain: s.domains[0]?.domain,
      })),
      totalStores: org.stores.length,
      activeStores: org.stores.filter(s => s.isActive).length,
    };
  }

  // Lets a rejected merchant edit their business details and re-enter the
  // review queue, per the confirmed product decision (see plan doc) rather
  // than forcing a brand-new registration. Deliberately reads
  // User.organizationId directly instead of resolveOrgId() — a rejected
  // merchant has no StoreStaff row and would never pass resolveOrgId's
  // approvalStatus==='approved' gate.
  async resubmitRegistration(
    userId: string,
    data: { businessName?: string; businessType?: string; phone?: string; gstin?: string; city?: string; state?: string }
  ) {
    const user = await this.prisma.user.findUnique({ where: { id: userId }, select: { organizationId: true } });
    if (!user?.organizationId) throw new NotFoundException('No organization found for this user');

    const org = await this.prisma.organization.findUnique({
      where: { id: user.organizationId },
      select: { approvalStatus: true },
    });
    if (org?.approvalStatus !== 'rejected') {
      throw new BadRequestException('Only a rejected registration can be resubmitted');
    }

    const updates: Record<string, unknown> = {
      approvalStatus: 'pending',
      rejectedAt: null,
      rejectedBy: null,
      rejectionReason: null,
      updatedBy: userId,
    };
    if (data.businessName !== undefined) updates['name'] = data.businessName.trim();
    if (data.businessType !== undefined) updates['businessType'] = data.businessType.trim();
    if (data.phone !== undefined) updates['phone'] = data.phone.trim();
    if (data.gstin !== undefined) updates['gstin'] = data.gstin.trim() || null;
    if (data.city !== undefined) updates['city'] = data.city.trim() || null;
    if (data.state !== undefined) updates['state'] = data.state.trim() || null;

    await this.prisma.organization.update({ where: { id: user.organizationId }, data: updates });
    return { success: true };
  }

  async getAllStaff(userId: string) {
    const orgId = await this.resolveOrgId(userId);

    const staff = await this.prisma.storeStaff.findMany({
      where: { organizationId: orgId, isActive: true, deletedAt: null },
      select: {
        id: true, role: true, joinedAt: true, isActive: true,
        user: { select: { id: true, name: true, email: true, role: true } },
        store: { select: { id: true, name: true, city: true, state: true } },
        staffPermissions: {
          where: { isActive: true },
          select: {
            id: true, granted: true,
            permission: { select: { id: true, code: true, name: true } },
          },
        },
      },
      orderBy: [{ store: { name: 'asc' } }, { role: 'asc' }],
    });

    return staff;
  }

  async getStoreStaff(storeId: string, userId: string) {
    const orgId = await this.resolveOrgId(userId);
    const store = await this.prisma.store.findFirst({
      where: { id: storeId, organizationId: orgId, deletedAt: null },
    });
    if (!store) throw new NotFoundException('Store not found');

    return this.prisma.storeStaff.findMany({
      where: { storeId, isActive: true, deletedAt: null },
      select: {
        id: true, role: true, joinedAt: true, isActive: true,
        user: { select: { id: true, name: true, email: true, role: true } },
        staffPermissions: {
          where: { isActive: true },
          select: {
            id: true, granted: true,
            permission: { select: { id: true, code: true, name: true } },
          },
        },
      },
      orderBy: { role: 'asc' },
    });
  }

  async addStaff(storeId: string, email: string, role: string, actorId: string) {
    const orgId = await this.resolveOrgId(actorId);
    const store = await this.prisma.store.findFirst({
      where: { id: storeId, organizationId: orgId, deletedAt: null },
    });
    if (!store) throw new NotFoundException('Store not found');

    // email is no longer a globally-unique column (migration 027 scoped it
    // per store for customer accounts) — findFirst instead of findUnique.
    // Safe here: staff invites only ever target merchant/platform accounts,
    // which stay effectively globally unique in practice (their storeId is
    // always the ambient-resolved default, see AuthRepository.create()).
    const user = await this.prisma.user.findFirst({ where: { email } });
    if (!user) throw new NotFoundException(`No user found with email ${email}`);

    const existing = await this.prisma.storeStaff.findUnique({
      where: { storeId_userId: { storeId, userId: user.id } },
    });

    if (existing) {
      return this.prisma.storeStaff.update({
        where: { id: existing.id },
        data: { isActive: true, role, deletedAt: null, updatedBy: actorId },
        select: { id: true, role: true, joinedAt: true, isActive: true,
          user: { select: { id: true, name: true, email: true, role: true } } },
      });
    }

    return this.prisma.storeStaff.create({
      data: {
        storeId, userId: user.id, organizationId: orgId,
        role, isActive: true, joinedAt: new Date(),
        createdBy: actorId, updatedBy: actorId,
      },
      select: { id: true, role: true, joinedAt: true, isActive: true,
        user: { select: { id: true, name: true, email: true, role: true } } },
    });
  }

  async updateStaff(staffId: string, role: string, userId: string) {
    const orgId = await this.resolveOrgId(userId);
    const staff = await this.prisma.storeStaff.findFirst({
      where: { id: staffId, organizationId: orgId, deletedAt: null },
    });
    if (!staff) throw new NotFoundException('Staff member not found');

    return this.prisma.storeStaff.update({
      where: { id: staffId },
      data: { role, updatedBy: userId },
      select: { id: true, role: true },
    });
  }

  async removeStaff(staffId: string, userId: string) {
    const orgId = await this.resolveOrgId(userId);
    const staff = await this.prisma.storeStaff.findFirst({
      where: { id: staffId, organizationId: orgId, deletedAt: null },
    });
    if (!staff) throw new NotFoundException('Staff member not found');

    await this.prisma.storeStaff.update({
      where: { id: staffId },
      data: { isActive: false, deletedAt: new Date(), deletedBy: userId, updatedBy: userId },
    });
    return { success: true };
  }

  async getStaffPermissions(staffId: string, userId: string) {
    const orgId = await this.resolveOrgId(userId);
    const staff = await this.prisma.storeStaff.findFirst({
      where: { id: staffId, organizationId: orgId },
      include: {
        staffPermissions: {
          where: { isActive: true },
          include: { permission: { select: { id: true, code: true, name: true, module: true } } },
        },
      },
    });
    if (!staff) throw new NotFoundException('Staff member not found');
    return staff.staffPermissions;
  }

  async setStaffPermissions(
    staffId: string,
    permissionCodes: string[],
    granted: boolean,
    userId: string,
  ) {
    const orgId = await this.resolveOrgId(userId);
    const staff = await this.prisma.storeStaff.findFirst({
      where: { id: staffId, organizationId: orgId },
    });
    if (!staff) throw new NotFoundException('Staff member not found');

    const permissions = await this.prisma.platformPermission.findMany({
      where: { code: { in: permissionCodes } },
      select: { id: true, code: true },
    });

    for (const perm of permissions) {
      const existing = await this.prisma.storeStaffPermission.findFirst({
        where: { storeStaffId: staffId, permissionId: perm.id },
        select: { id: true },
      });

      if (existing) {
        await this.prisma.storeStaffPermission.update({
          where: { id: existing.id },
          data: { granted, isActive: true, updatedBy: userId },
        });
      } else {
        await this.prisma.storeStaffPermission.create({
          data: {
            storeStaffId: staffId, permissionId: perm.id, storeId: staff.storeId,
            granted, isActive: true, createdBy: userId, updatedBy: userId,
          },
        });
      }
    }

    return { updated: permissions.length };
  }

  // ── Store CRUD ────────────────────────────────────────────────────────────

  private slugify(name: string): string {
    return name.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  }

  private async uniqueSlug(base: string, orgId: string, excludeId?: string): Promise<string> {
    const slug = this.slugify(base);
    let attempt = 0;
    while (true) {
      const candidate = attempt === 0 ? slug : `${slug}-${attempt}`;
      // Scoped to this organization only — store slugs are unique per-org
      // (@@unique([organizationId, slug]), see migration 023), not globally.
      // Two different merchants must each be able to name a store "Downtown".
      const existing = await this.prisma.store.findFirst({
        where: { organizationId: orgId, slug: candidate, ...(excludeId ? { id: { not: excludeId } } : {}) },
        select: { id: true },
      });
      if (!existing) return candidate;
      attempt++;
    }
  }

  // ── Subdomain (multi-tenant) ─────────────────────────────────────────────
  // See plan.md §3 — reserved-word list and format rules are shared with the
  // client-side check via @cart/shared-types so both sides agree without
  // duplicating the list.

  async checkSubdomain(value: string): Promise<SubdomainAvailability> {
    const v = value.trim().toLowerCase();
    const formatError = validateSubdomainFormat(v);
    if (formatError) return { available: false, reason: 'invalid-format' };
    if (RESERVED_SUBDOMAINS.includes(v)) return { available: false, reason: 'reserved' };

    const existing = await this.prisma.storeDomain.findUnique({
      where: { domain: `${v}.${TENANT_DOMAIN_SUFFIX}` },
      select: { id: true },
    });
    return existing ? { available: false, reason: 'taken' } : { available: true };
  }

  private async assertSubdomainAvailable(value: string): Promise<string> {
    const v = value.trim().toLowerCase();
    const result = await this.checkSubdomain(v);
    if (!result.available) {
      const messages: Record<string, string> = {
        'invalid-format': 'Subdomain must be 3-63 lowercase letters, numbers, or hyphens',
        reserved: 'This subdomain is reserved',
        taken: 'This subdomain is already taken',
      };
      throw new BadRequestException(messages[result.reason ?? 'invalid-format']);
    }
    return v;
  }

  async createStore(
    userId: string,
    data: { name: string; subdomain: string; city?: string; state?: string; country?: string; currency?: string; timezone?: string },
  ) {
    const orgId = await this.resolveOrgId(userId);

    const org = await this.prisma.organization.findUniqueOrThrow({
      where: { id: orgId },
      select: { maxStores: true },
    });
    if (org.maxStores != null) {
      const storeCount = await this.prisma.store.count({ where: { organizationId: orgId, deletedAt: null } });
      if (storeCount >= org.maxStores) {
        throw new BadRequestException(
          `Store limit reached (max ${org.maxStores}) — contact support to increase your quota`
        );
      }
    }

    const slug  = await this.uniqueSlug(data.name, orgId);
    const subdomain = await this.assertSubdomainAvailable(data.subdomain);

    const store = await this.prisma.$transaction(async (tx) => {
      const created = await tx.store.create({
        data: {
          organizationId: orgId,
          name: data.name,
          slug,
          city:            data.city    ?? null,
          state:           data.state   ?? null,
          country:         data.country ?? 'IN',
          defaultCurrency: data.currency ?? 'INR',
          timezone:        data.timezone ?? 'Asia/Kolkata',
          defaultLocale:   'en-IN',
          isActive:        true,
          createdBy:       userId,
          updatedBy:       userId,
        },
      });

      // Live the moment this commits — no external DNS/cert provisioning
      // step, the wildcard already covers it (plan.md §3.4).
      await tx.storeDomain.create({
        data: {
          storeId:            created.id,
          domain:             `${subdomain}.${TENANT_DOMAIN_SUFFIX}`,
          isPrimary:          true,
          verificationStatus: 'verified',
          createdBy:          userId,
          updatedBy:          userId,
        },
      });

      // Same transaction as the store itself — a store should never exist
      // without its default tax slabs (previously it always did, since
      // nothing provisioned these at all; see migration 024 for the
      // one-time backfill on stores created before this fix).
      await tx.taxSlabProfile.createMany({
        data: DEFAULT_TAX_SLABS.map((slab) => ({
          storeId: created.id,
          code: slab.code,
          name: slab.name,
          hsnGroup: slab.hsnGroup,
          igstRateBps: slab.igst,
          cgstRateBps: slab.cgst,
          sgstRateBps: slab.sgst,
          createdBy: userId,
          updatedBy: userId,
        })),
      });

      // The creator's staff row belongs INSIDE this transaction.
      //
      // It used to run after the commit, so anything that threw here left a
      // store with no owner: the caller saw a 500 while the store was already
      // live, and the merchant was then refused by AdminAccessGuard — which
      // grants access on an active StoreStaff row — with "Admin access is
      // required" on every store screen. Both reported bugs, one cause.
      //
      // upsert rather than create: (storeId, userId) is unique, so a retried or
      // double-submitted creation would otherwise fail on the second attempt.
      await tx.storeStaff.upsert({
        where: { storeId_userId: { storeId: created.id, userId } },
        update: { isActive: true, role: 'merchant', deletedAt: null, updatedBy: userId },
        create: {
          storeId:        created.id,
          userId,
          organizationId: orgId,
          role:           'merchant',
          isActive:       true,
          joinedAt:       new Date(),
          createdBy:      userId,
          updatedBy:      userId,
        },
      });

      return created;
    });

    return { ...store, domain: `${subdomain}.${TENANT_DOMAIN_SUFFIX}` };
  }

  async updateStore(
    storeId: string,
    userId: string,
    data: { name?: string; city?: string; state?: string; country?: string; currency?: string; timezone?: string },
  ) {
    const orgId = await this.resolveOrgId(userId);
    const store = await this.prisma.store.findFirst({
      where: { id: storeId, organizationId: orgId, deletedAt: null },
    });
    if (!store) throw new NotFoundException('Store not found');

    const updates: Record<string, unknown> = { updatedBy: userId };
    if (data.name)     { updates['name'] = data.name; updates['slug'] = await this.uniqueSlug(data.name, orgId, storeId); }
    if (data.city     !== undefined) updates['city']            = data.city;
    if (data.state    !== undefined) updates['state']           = data.state;
    if (data.country  !== undefined) updates['country']         = data.country;
    if (data.currency !== undefined) updates['defaultCurrency'] = data.currency;
    if (data.timezone !== undefined) updates['timezone']        = data.timezone;

    return this.prisma.store.update({ where: { id: storeId }, data: updates });
  }

  async deleteStore(storeId: string, userId: string) {
    const orgId = await this.resolveOrgId(userId);
    const store = await this.prisma.store.findFirst({
      where: { id: storeId, organizationId: orgId, deletedAt: null },
    });
    if (!store) throw new NotFoundException('Store not found');

    await this.prisma.store.update({
      where: { id: storeId },
      data: { deletedAt: new Date(), deletedBy: userId, isActive: false, updatedBy: userId },
    });
    return { success: true };
  }

  async toggleStoreActive(storeId: string, active: boolean, userId: string) {
    const orgId = await this.resolveOrgId(userId);
    const store = await this.prisma.store.findFirst({
      where: { id: storeId, organizationId: orgId, deletedAt: null },
    });
    if (!store) throw new NotFoundException('Store not found');

    await this.prisma.store.update({
      where: { id: storeId },
      data: {
        isActive:       active,
        deactivatedAt:  active ? null : new Date(),
        deactivatedBy:  active ? null : userId,
        updatedBy:      userId,
      },
    });
    return { success: true, isActive: active };
  }
}
